/* Apollith Studio Core - shared, product-agnostic helpers for the route-based
 * product editors (Route Print Studio and Shared Miles).
 *
 * This is the reusable foundation the spec calls for: the new Shared Miles editor
 * (shared-miles.js) is built ENTIRELY on these helpers rather than duplicating them.
 * Every function here is product-agnostic: no single-route or single-participant
 * assumption, no DOM ownership beyond what is passed in. Route Print Studio can be
 * migrated onto this same core in a follow-up (verified against the live editor).
 *
 * Exposed as window.ApollithStudio. Vanilla, no framework, no build step. */
(function () {
  'use strict';
  if (window.ApollithStudio) return; // load once even if two products are on a page

  // ---- backend client (through the signed Shopify App Proxy) ---------------
  // Returns a client bound to a proxy base. Every request has a deadline so a request
  // that never settles (flaky mobile network) can never wedge a flow forever.
  function createClient(proxyBase) {
    var PROXY = (proxyBase || '/apps/route-studio').replace(/\/$/, '');
    function request(path, opts) {
      opts = opts || {};
      var init = { method: opts.method || 'GET', headers: opts.headers || {} };
      if (opts.json !== undefined) { init.headers['Content-Type'] = 'application/json'; init.body = JSON.stringify(opts.json); }
      else if (opts.body !== undefined) { init.body = opts.body; }
      var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
      var timer = null;
      if (ctrl) { init.signal = ctrl.signal; timer = setTimeout(function () { ctrl.abort(); }, opts.timeoutMs || 20000); }
      var clear = function () { if (timer) { clearTimeout(timer); timer = null; } };
      return fetch(PROXY + path, init).then(function (res) {
        return res.text().then(function (text) {
          clear();
          var data = {};
          try { data = text ? JSON.parse(text) : {}; } catch (e) { data = {}; }
          if (!res.ok) {
            var msg = (data && data.error && data.error.message) || 'Something went wrong. Please try again.';
            var err = new Error(msg); err.code = data && data.error && data.error.code; throw err;
          }
          return data;
        });
      }).catch(function (e) {
        clear();
        if (e && e.name === 'AbortError') { var t = new Error('The connection timed out. Check your signal and try again.'); t.code = 'timeout'; throw t; }
        throw e;
      });
    }
    return { request: request, base: PROXY };
  }

  // ---- small utilities -----------------------------------------------------
  function esc(s) { return String(s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function debounce(fn, ms) { var t; return function () { var a = arguments, self = this; clearTimeout(t); t = setTimeout(function () { fn.apply(self, a); }, ms); }; }
  function pad2(n) { return (n < 10 ? '0' : '') + n; }
  function parseJson(node) { try { return node ? JSON.parse(node.textContent) : {}; } catch (e) { return {}; } }
  // Restart a one-shot CSS animation by re-adding its class (forced reflow) so a keyframe
  // keyed on the class replays even when nothing else changed. No-op under reduced motion
  // (the keyframe rule is gated) and harmless if the class lingers.
  function restartAnim(node, cls) { if (!node) return; node.classList.remove(cls); void node.offsetWidth; node.classList.add(cls); }

  // ---- money + unit formatting (mirrors Route Print Studio exactly) --------
  function formatMoney(pence) {
    try { return new Intl.NumberFormat('en-GB', { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'GBP' }).format(pence / 100); }
    catch (e) { return '£' + (pence / 100).toFixed(2); }
  }
  function distanceUnitLabel(unit) { return unit === 'kilometres' ? 'km' : 'mi'; }
  function formatDistance(metres, unit) {
    var v = unit === 'kilometres' ? metres / 1000 : metres / 1609.344;
    // Match Strava: two decimals rounded DOWN; +1e-6 guards the float-floor edge.
    return (Math.floor(v * 100 + 1e-6) / 100).toString();
  }
  function formatDistanceWithUnit(metres, unit) {
    if (typeof metres !== 'number' || !isFinite(metres)) return '';
    return formatDistance(metres, unit) + ' ' + distanceUnitLabel(unit);
  }
  function formatElevation(metres, unit) {
    if (typeof metres !== 'number' || !isFinite(metres)) return '';
    return unit === 'kilometres' ? Math.round(metres) + ' m' : Math.round(metres * 3.280839895) + ' ft';
  }
  function formatDate(iso) {
    try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }); }
    catch (e) { return ''; }
  }
  function formatDuration(totalSec) {
    var s = Math.round(totalSec); if (!(s > 0)) return '';
    var h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    return h > 0 ? h + ':' + pad2(m) + ':' + pad2(sec) : m + ':' + pad2(sec);
  }
  function formatPace(movingSec, metres, unit) {
    if (!movingSec || !metres) return '';
    var unitM = unit === 'kilometres' ? 1000 : 1609.344;
    var secPerUnit = movingSec / (metres / unitM);
    var m = Math.floor(secPerUnit / 60), sec = Math.round(secPerUnit % 60);
    if (sec === 60) { m += 1; sec = 0; }
    return m + ':' + pad2(sec) + ' /' + (unit === 'kilometres' ? 'km' : 'mi');
  }

  // ---- ARIA radiogroup: roving tabindex + arrow-key navigation -------------
  function wireRadioGroup(container, radioSel, selectFromEl) {
    if (!container) return;
    container.addEventListener('keydown', function (e) {
      if (['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'].indexOf(e.key) === -1) return;
      var list = Array.prototype.slice.call(container.querySelectorAll(radioSel)).filter(function (r) { return r.getAttribute('aria-disabled') !== 'true'; });
      if (!list.length) return;
      e.preventDefault();
      var cur = list.findIndex(function (r) { return r.tabIndex === 0; });
      if (cur < 0) cur = list.findIndex(function (r) { return r.getAttribute('aria-checked') === 'true'; });
      if (cur < 0) cur = 0;
      var to = cur;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') to = (cur + 1) % list.length;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') to = (cur - 1 + list.length) % list.length;
      else if (e.key === 'Home') to = 0;
      else if (e.key === 'End') to = list.length - 1;
      var target = list[to];
      selectFromEl(target);
      target.focus();
    });
  }

  // ---- session persistence (survive an accidental refresh) -----------------
  // Only the job id is ever stored; no route data touches the browser.
  function makeSession(key) {
    return {
      get: function () { try { var raw = sessionStorage.getItem(key); return raw ? JSON.parse(raw) : null; } catch (e) { return null; } },
      set: function (obj) { try { sessionStorage.setItem(key, JSON.stringify(obj)); } catch (e) { /* private mode */ } },
      clear: function () { try { sessionStorage.removeItem(key); } catch (e) { /* noop */ } }
    };
  }

  // ---- cart add (idempotent) + drawer morph --------------------------------
  // Shared cart mechanics: the same idempotency guard (skip a re-add of a signature
  // already in the cart, avoiding a double charge on a lost mobile response), the same
  // drawer section-morph, and the same deadline. `signature` is the load-bearing
  // dedupe key (a job's cart-line signature). Returns the cart JSON.
  function cartRootPath() { return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/'; }
  function cartHasSignature(signature) {
    if (!signature) return Promise.resolve(false);
    return fetch(cartRootPath() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r && r.ok ? r.json() : null; }, function () { return null; })
      .catch(function () { return null; })
      .then(function (cart) {
        return !!(cart && Array.isArray(cart.items) && cart.items.some(function (it) {
          return it && it.properties && it.properties['_route_print_job_signature'] === signature;
        }));
      });
  }
  function collectDrawerSections() {
    var ids = [];
    try { document.dispatchEvent(new CustomEvent('cart:grouped-sections', { detail: { sections: ids } })); } catch (e) { /* no drawer */ }
    return ids.filter(function (v, i, a) { return v && a.indexOf(v) === i; });
  }
  function cartAdd(items, opts) {
    opts = opts || {};
    var root = cartRootPath();
    var body = { items: items };
    var sectionIds = collectDrawerSections();
    if (sectionIds.length) { body.sections = sectionIds.join(','); body.sections_url = window.location.pathname; }
    var ctrl = ('AbortController' in window) ? new AbortController() : null;
    var to = ctrl ? setTimeout(function () { ctrl.abort(); }, opts.timeoutMs || 25000) : null;
    return fetch(root + 'cart/add.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal: ctrl ? ctrl.signal : undefined
    }).then(function (r) {
      if (to) clearTimeout(to);
      return r.json().catch(function () { return {}; }).then(function (j) {
        if (!r.ok) throw new Error(j.description || j.message || 'Could not add to basket. Please try again.');
        return j;
      });
    }, function (err) {
      if (to) clearTimeout(to);
      throw new Error(err && err.name === 'AbortError' ? 'That took too long. Please check your connection and try again.' : ((err && err.message) || 'Could not add to basket. Please try again.'));
    });
  }
  // Notify the theme the cart changed (updates the header count + drawer and auto-opens
  // it), mirroring the theme's own CartAddEvent. Falls back to opening the drawer directly.
  function notifyCartUpdated(cartData, lineCount, sourceId) {
    var sections = cartData && cartData.sections ? cartData.sections : undefined;
    var delta = lineCount > 0 ? lineCount : 1;
    var dispatched = false;
    try {
      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: { resource: cartData || {}, sourceId: sourceId || 'apollith-studio', data: { source: sourceId || 'apollith-studio', itemCount: delta, isIncremental: true, sections: sections } }
      }));
      dispatched = true;
    } catch (e) { /* fall through */ }
    if (!dispatched) openCartDrawer();
    else setTimeout(function () { var dlg = document.getElementById('cart-drawer-dialog'); if (!dlg || !dlg.open) openCartDrawer(); }, 80);
  }
  function openCartDrawer() {
    var drawer = document.getElementById('CartDrawer');
    if (drawer && typeof drawer.showDialog === 'function') { try { drawer.showDialog(); return; } catch (e) {} }
    var btn = document.querySelector('.cart-drawer-button');
    if (btn) { try { btn.click(); return; } catch (e) {} }
    window.location.href = (window.Shopify && Shopify.routes && Shopify.routes.cart_url) || (cartRootPath() + 'cart');
  }

  window.ApollithStudio = {
    createClient: createClient,
    esc: esc,
    debounce: debounce,
    pad2: pad2,
    parseJson: parseJson,
    restartAnim: restartAnim,
    formatMoney: formatMoney,
    fmt: {
      distance: formatDistance,
      distanceUnitLabel: distanceUnitLabel,
      distanceWithUnit: formatDistanceWithUnit,
      elevation: formatElevation,
      date: formatDate,
      duration: formatDuration,
      pace: formatPace
    },
    wireRadioGroup: wireRadioGroup,
    makeSession: makeSession,
    cart: { add: cartAdd, hasSignature: cartHasSignature, notifyUpdated: notifyCartUpdated, openDrawer: openCartDrawer }
  };
})();
